import { useEffect, useState } from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { unstable_usePrompt } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Recycle, Warehouse } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import {
    BarcodeType,
    GetDetailedProductQuery,
    GetProductPricesQuery,
    GetProductPriceRangesQuery,
    GetTagsQuery,
    GetActiveCategoriesQuery,
    GetActiveOrganizationsQuery,
    GetSuppliersQuery,
    useUpdateProductMutation,
    ProductUpdateInput,
    GetProductPricesDocument,
} from 'data/__generated__';

import { PageTitle } from 'components/PageTitle';
import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';

import { DimensionsSection } from './DimensionsSection';
import { GeneralInfoSection } from './GeneralInfoSection';
import { SalesInfoSection } from './SalesInfoSection';
import { StatesSection } from './StatesSection';
import { NutritionalInfoSection } from './NutritionalInfoSection';
import { StockAndSupplierInfoSection } from './StockAndSupplierInfoSection';
import { TagsAndBadgesSection } from './TagsAndBadgesSection';
import { EndOfLifeProductModal } from 'pages/Products/ProductDetails/components/EndOfLifeProductModal';
import { formatFormArray, formatMultilineToArray } from 'pages/Products/utils';
import { ProductGroupSection } from './ProductGroupSection';

import { ProductArrangementsSection } from './ProductArrangementsSection';
import { LocationsSection } from './LocationsSection';
import { ColumnStocksSection } from './ColumnStocksSection';
import { RecipeSection, RecipeIngredient } from './RecipeSection';
import { SectionContainer } from 'components/DetailsView/Section';
import { AuditLogs } from 'components/AuditLogs';
import { ENV_DEV } from 'constants/environment';
import { WAREHOUSE_PREP_FLOW, WAREHOUSE_SITE_TO_COLUMN_ID } from 'pages/Stocks/constants';

type Merge<A, B> = Omit<A, keyof B> & B;

type FormData = Merge<
    ProductUpdateInput,
    {
        access: { value: string; label: string }[];
        barcodes: { barcode: string; type: BarcodeType }[];
        diets: { value: string; label: string }[];
        preferences: { value: string; label: string }[];
        supplierShortageInfo: {
            inShortage: boolean;
            returnDate: string | null;
        };
        tagOptions: { value: string; label: string }[]; //object type used with UI Select, stocking as strings in DB
        searchKeywords: string | null;
        recipeIngredients: RecipeIngredient[];
    }
>;

export const ProductGlobalForm = ({
    tags,
    categories,
    organizations,
    detailedProduct,
    productPrices,
    productPriceRanges,
    suppliers,
}: {
    tags: GetTagsQuery['tags'];
    categories: GetActiveCategoriesQuery['activeCategories'];
    organizations: GetActiveOrganizationsQuery['activeOrganizations'];
    detailedProduct: NonNullable<GetDetailedProductQuery['detailedProduct']>;
    productPrices: GetProductPricesQuery['productPrices'];
    productPriceRanges: GetProductPriceRangesQuery['productPriceRanges'];
    suppliers: GetSuppliersQuery['suppliers'];
}) => {
    const {
        _id,
        displayKg,
        fullname,
        access,
        barcodes,
        diets,
        portion,
        preferences,
        tagIds,
        category,
        suppliers: productSuppliers,
        flow,
    } = detailedProduct;
    const [modalIsOpen, setIsOpen] = useState(false);

    const [updateProduct, { loading: updateLoading }] = useUpdateProductMutation();

    const openModal = () => {
        setIsOpen(true);
    };

    const methods = useForm<FormData>({
        // need to define defaultValues here in order for useFieldArray to work
        defaultValues: {
            categoryId: category._id,
            subcategoryId: detailedProduct.subcategory?._id ?? null,
            // need to transform string to object because useFieldArray doesn't support strings in arrays
            barcodes,
            access: access.reduce<{ value: string; label: string }[]>((acc, organizationId) => {
                const activeOrganization = organizations.find(({ _id }) => _id === organizationId);
                if (activeOrganization) {
                    acc.push({
                        value: organizationId,
                        label: activeOrganization.name,
                    });
                }

                return acc;
            }, []),
            tagOptions: tagIds.map((tagId) => {
                const tag = tags.find(({ _id }) => _id === tagId);
                return {
                    value: tagId,
                    label: tag?.name || 'Sans nom',
                };
            }),
            preferences: preferences.map(({ _id, name }) => {
                return {
                    value: _id,
                    label: name,
                };
            }),
            diets: diets.map(({ _id, name }) => {
                return {
                    value: _id,
                    label: name,
                };
            }),
            suppliers: productSuppliers,
            zeroWasteProductTemplateId: detailedProduct.zeroWasteProductTemplateId,
            recipeIngredients:
                detailedProduct.recipe?.ingredients.map((ingredient) => ({
                    productId: ingredient.productId,
                    quantity: ingredient.quantity,
                    hotDrinksIngredientUnit: ingredient.hotDrinksIngredientUnit,
                })) || [],
        },
    });

    const {
        handleSubmit,
        formState: { isDirty, isSubmitSuccessful },
        reset,
    } = methods;

    unstable_usePrompt({
        message: 'Êtes-vous sûr.e de vouloir quitter cette page sans enregistrer ?',
        when: ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
    });

    // resetting the form here because it shouldn't be done
    // inside the handleSubmit function (#readthedocs)
    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({}, { keepValues: true });
        }
    }, [isSubmitSuccessful, reset]);

    const onSubmit = handleSubmit(
        async (fields) => {
            fields.isBeingTested = fields.isBeingTested || detailedProduct.isBeingTested;

            const tagIds = formatFormArray(fields.tagOptions);
            const formattedAccess = formatFormArray(fields.access);
            const formattedBarcodes = fields.barcodes.map(({ barcode, type }) => ({ barcode, type }));
            const formattedDiets = formatFormArray(fields.diets);
            const formattedPreferences = formatFormArray(fields.preferences);
            const formattedSearchKeywords = formatMultilineToArray(fields.searchKeywords || '');
            const formattedRecipeIngredients = fields.recipeIngredients;

            const { tagOptions, recipeIngredients, ...fieldsWithoutTags } = fields;

            if (!fields.categoryId) {
                toast.error('Un produit doit être associé à une catégorie.');
                return;
            }

            const { data } = await updateProduct({
                variables: {
                    productId: detailedProduct._id,
                    fields: {
                        ...fieldsWithoutTags,
                        access: formattedAccess,
                        barcodes: formattedBarcodes,
                        diets: formattedDiets,
                        preferences: formattedPreferences,
                        searchKeywords: formattedSearchKeywords,
                        tagIds,
                        state: detailedProduct.state,
                        recipe:
                            formattedRecipeIngredients.length > 0 ? { ingredients: formattedRecipeIngredients } : null,
                    },
                },
                refetchQueries: [{ query: GetProductPricesDocument, variables: { productId: detailedProduct._id } }],
            });
            if (data) {
                const {
                    updateProduct: { success, errors, formErrors },
                } = data;

                errors?.forEach((error, index) => {
                    toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
                });

                if (success) {
                    toast.success(`Le produit "${fields.name}" a bien été modifié !`);
                } else {
                    if (formErrors) {
                        formErrors.forEach(({ sectionName, sectionErrors }, index) => {
                            toast.error(
                                <span key={index}>
                                    Erreur dans la section "{sectionName}" :
                                    {sectionErrors.map(({ fieldName, fieldError }, index) => (
                                        <span key={index}>
                                            <br />- {fieldName} : "{fieldError}"
                                        </span>
                                    ))}
                                </span>,
                                { autoClose: false },
                            );
                        });

                        if (formErrors.length > 1) {
                            toast.info('Cliquez pour fermer toutes les notifications', {
                                autoClose: false,
                                onClick: () => toast.dismiss(),
                            });
                        }
                    } else {
                        throw Error("Une erreur inconnue s'est produite");
                    }
                }
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        },
        (_errors) => {
            toast.error("Le formulaire n'est pas valide. Veuillez corriger les erreurs avant de continuer.");
        },
    );

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Produit',
            items: [
                {
                    label: 'Gérer les produits en fin de vie',
                    icon: <Recycle />,
                    onClick: openModal,
                    disabled: updateLoading,
                },
                {
                    label: "Voir les stocks dans l'entrepôt",
                    icon: <Warehouse />,
                    to: `/column/${WAREHOUSE_SITE_TO_COLUMN_ID[WAREHOUSE_PREP_FLOW[flow]]}/stocks/${_id}`,
                },
            ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/products' }],
        },
    ];

    return (
        <>
            {modalIsOpen ? (
                <EndOfLifeProductModal
                    fullname={fullname}
                    productId={_id}
                    displayKg={displayKg}
                    categoryId={category._id}
                    portion={portion}
                    setIsOpen={setIsOpen}
                />
            ) : null}
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.productDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={updateLoading}
                            saveTitle="Mettre à jour"
                            saveDataTest="submit-button"
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection product={detailedProduct} categories={categories} />
                            <SalesInfoSection
                                product={detailedProduct}
                                productPrices={productPrices}
                                productPriceRanges={productPriceRanges}
                                organizations={organizations}
                            />
                            <TagsAndBadgesSection tags={tags} />
                            <NutritionalInfoSection product={detailedProduct} />
                            <DimensionsSection product={detailedProduct} />
                            <RecipeSection product={detailedProduct} />
                            <StockAndSupplierInfoSection product={detailedProduct} suppliers={suppliers} />
                            <LocationsSection product={detailedProduct} />
                            <ColumnStocksSection productId={detailedProduct._id} />
                            <StatesSection
                                product={detailedProduct}
                                isDirty={isDirty && window.__TOTENV__.ENV !== ENV_DEV}
                            />
                            <ProductGroupSection product={detailedProduct} />
                            <ProductArrangementsSection product={detailedProduct} />
                            <SectionContainer title="Audit logs" isInitiallyOpen={false}>
                                <AuditLogs collectionDocumentId={_id} />
                            </SectionContainer>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
